"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { Menu, X, Search } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
function HeaderMenuConsumer({
  onItemClick
}: {
  onItemClick?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="222573" className="flex flex-col md:flex-row gap-4 md:gap-2">
        <div className="h-9 w-24 md:w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-24 md:w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-24 md:w-16 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="max-w-full w-full md:w-auto">
      <NavigationMenuList className="flex-col md:flex-row items-start md:items-center gap-4 md:gap-2 w-full">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i} className="w-full md:w-auto">
            <NavigationMenuLink asChild className="bg-transparent hover:bg-transparent hover:text-[#efc318] data-[active=true]:text-[#efc318] text-white font-default text-lg md:text-sm font-bold transition-colors block w-full py-2 md:py-0">
              <Link to={item.href || "#"} onClick={onItemClick}>{item.label}</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-secondary/90 backdrop-blur-md border-b border-border/10" : "bg-secondary border-b border-transparent"}`}>
      <div className="container mx-auto px-6 md:px-8 py-4 flex items-center justify-between gap-4 md:gap-8">
        <Link to="/" className="flex-shrink-0">
          <WvcLogo className="h-12 md:h-16 w-auto" />
        </Link>

        <div className="flex items-center justify-end gap-4 md:gap-6 flex-1">
          {}
          <div className="hidden md:block">
            <MenuProvider menu_id="31">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden sm:block">
            <Select defaultValue="en">
              <SelectTrigger className="w-[65px] h-8 border-none bg-transparent hover:bg-accent/20 focus:ring-0 px-2 text-white font-default font-bold">
                <SelectValue placeholder="EN" />
              </SelectTrigger>
              <SelectContent className="bg-secondary border-border/20 text-white">
                <SelectItem value="en" className="font-default font-bold focus:bg-accent/20 focus:text-white">EN</SelectItem>
                <SelectItem value="fr" className="font-default font-bold focus:bg-accent/20 focus:text-white">FR</SelectItem>
                <SelectItem value="es" className="font-default font-bold focus:bg-accent/20 focus:text-white">ES</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {}
          <div className="hidden lg:block w-48">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-white/40" />
              <Input type="search" placeholder="Search..." className="h-8 pl-8 rounded-lg border-border/20 bg-white/5 text-sm text-white placeholder:text-white/40 focus-visible:ring-1 focus-visible:ring-[#efc318]" />
            </div>
          </div>

          {}
          <div className="md:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors" aria-label="Open Menu">
                  <Menu className="size-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-xs bg-secondary border-none p-0 flex flex-col">
                <SheetHeader className="p-6 border-b border-white/10 flex flex-row items-center justify-between">
                  <SheetTitle className="text-white font-bold text-xl">Menu</SheetTitle>
                </SheetHeader>
                
                <div className="flex-1 overflow-y-auto p-6">
                  <MenuProvider menu_id="31">
                    <HeaderMenuConsumer onItemClick={() => setMobileMenuOpen(false)} />
                  </MenuProvider>
                  
                  <div className="mt-8 pt-8 border-t border-white/10 space-y-6">
                    <div className="space-y-2">
                      <p className="text-white/40 text-xs font-bold uppercase tracking-widest">Search</p>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-white/40" />
                        <Input type="search" placeholder="Search wellbeing..." className="h-10 pl-10 rounded-lg border-border/20 bg-white/5 text-white placeholder:text-white/40" />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-white/40 text-xs font-bold uppercase tracking-widest">Language</p>
                      <div className="flex gap-2">
                        {['EN', 'FR', 'ES'].map(lang => <button key={lang} className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${lang === 'EN' ? 'bg-[#efc318] text-secondary' : 'bg-white/5 text-white hover:bg-white/10'}`}>
                            {lang}
                          </button>)}
                      </div>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}